/**
 * Campaign history helpers (CDMO claim + sponsor invite).
 * First-party campaign history metrics (delivery analytics live in Resend or Instantly).
 */

export type CampaignHistoryType = 'sponsor_invite' | 'cdmo_claim';

export type CampaignEmailProvider = 'resend' | 'instantly';

/** Label for campaign history / admin UI. */
export function formatCampaignEmailProvider(
  provider?: string | null,
): 'Resend' | 'Instantly' {
  return provider === 'instantly' ? 'Instantly' : 'Resend';
}

export type CampaignHistoryTypeFilter = 'all' | CampaignHistoryType;

/** Default page size for Campaign → History table. */
export const CAMPAIGN_HISTORY_DEFAULT_PAGE_SIZE = 20;

/** PostgREST `.in()` filter chunk size for unsubscribe lookups. */
export const CAMPAIGN_HISTORY_EMAIL_IN_CHUNK_SIZE = 100;

export interface CampaignHistoryIndexEntry {
  campaignType: CampaignHistoryType;
  id: string;
  dateStarted: string;
}

export type CampaignHistoryStatus =
  | 'active'
  | 'completed'
  | 'stopped_account_created'
  | 'stopped_unsubscribed'
  | 'failed';

export interface CampaignHistoryConversion {
  name: string;
  company?: string;
  email: string;
  /** Original invited email when signup used a different address (sponsor invites). */
  invitedEmail?: string;
}

export interface CampaignHistoryRecipient {
  email: string;
  status: 'converted' | 'clicked' | 'sent' | 'unsubscribed' | string;
  stage?: number;
  signupEmail?: string | null;
  /** CDMO claim batches: facility name for the recipient row. */
  companyName?: string;
}

/** Metrics we can derive from app DB state (not Resend delivery/open/click analytics). */
export interface CampaignHistoryMetrics {
  /** Campaign emails sent (from {@code email_stage} / {@code claim_email_stage}). */
  sent: number;
  /** Invite or claim CTA link clicks ({@code link_clicked_at}). */
  linkClicks: number;
  unsubscribed: number;
  signUps: number;
}

const STATUS_LABELS: Record<CampaignHistoryStatus, string> = {
  active: 'Active',
  completed: 'Completed',
  stopped_account_created: 'Stopped — account created',
  stopped_unsubscribed: 'Stopped — unsubscribed',
  failed: 'Failed',
};

/** Maps raw DB / derived status strings to PDF history labels. */
export function formatCampaignHistoryStatus(raw: string): string {
  const key = raw.toLowerCase().replace(/\s+/g, '_') as CampaignHistoryStatus;
  if (key in STATUS_LABELS) {
    return STATUS_LABELS[key as CampaignHistoryStatus];
  }
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function countSponsorEmailsSent(
  recipients: { email_stage?: number | null }[],
): number {
  return recipients.filter((r) => (r.email_stage ?? 0) >= 1).length;
}

export function countLinkClicks(
  recipients: { link_clicked_at?: string | null }[],
): number {
  return recipients.filter((r) => r.link_clicked_at).length;
}

export function countUnsubscribed(
  recipients: { unsubscribed_at?: string | null }[],
): number {
  return recipients.filter((r) => r.unsubscribed_at).length;
}

export function maxEmailStage(recipients: { email_stage?: number | null }[]): number {
  return recipients.reduce((m, r) => Math.max(m, r.email_stage ?? 0), 0);
}

/** Derive sponsor batch status from campaign row + recipient funnel. */
export function deriveSponsorCampaignStatus(input: {
  campaignStatus: string;
  recipients: {
    sequence_active?: boolean;
    converted_user_id?: string | null;
    unsubscribed_at?: string | null;
    email_stage?: number | null;
    sequence_stopped_reason?: string | null;
  }[];
}): CampaignHistoryStatus | string {
  const { recipients } = input;
  const normalized = input.campaignStatus?.toLowerCase() ?? 'active';

  if (normalized === 'failed') return 'failed';
  if (recipients.some((r) => r.converted_user_id)) return 'stopped_account_created';
  if (recipients.some((r) => r.unsubscribed_at || r.sequence_stopped_reason === 'unsubscribed')) {
    return 'stopped_unsubscribed';
  }
  if (recipients.length > 0 && recipients.every((r) => !r.sequence_active)) {
    return 'completed';
  }
  if (normalized === 'completed' || normalized === 'stopped') return 'completed';
  return 'active';
}

/** Derive CDMO per-profile campaign status for history. */
export function deriveCdmoCampaignStatus(row: {
  campaign_active?: boolean | null;
  user_id?: string | null;
  claim_email_stage?: number | null;
}): CampaignHistoryStatus | string {
  if (row.user_id) return 'stopped_account_created';
  if (row.campaign_active) return 'active';
  if ((row.claim_email_stage ?? 0) >= 3) return 'completed';
  return 'completed';
}

export function buildSponsorHistoryMetrics(
  recipients: {
    email_stage?: number | null;
    link_clicked_at?: string | null;
    unsubscribed_at?: string | null;
    converted_user_id?: string | null;
  }[],
): CampaignHistoryMetrics {
  return {
    sent: countSponsorEmailsSent(recipients),
    linkClicks: countLinkClicks(recipients),
    unsubscribed: countUnsubscribed(recipients),
    signUps: recipients.filter((r) => r.converted_user_id).length,
  };
}

export function buildCdmoHistoryMetrics(row: {
  claim_email_stage?: number | null;
  claim_link_clicked_at?: string | null;
  user_id?: string | null;
  unsubscribed?: boolean | null;
}): CampaignHistoryMetrics {
  return {
    sent: (row.claim_email_stage ?? 0) >= 1 ? 1 : 0,
    linkClicks: row.claim_link_clicked_at ? 1 : 0,
    unsubscribed: row.unsubscribed ? 1 : 0,
    signUps: row.user_id ? 1 : 0,
  };
}

/** Same order as claim campaign edge functions (`primary_email` then `email`). */
export function cdmoClaimRecipientEmail(row: {
  email?: string | null;
  primary_email?: string | null;
}): string {
  return (row.primary_email || row.email || '').trim().toLowerCase();
}

/** Group profiles started in the same admin "Start campaign" action (shared `claim_campaign_started_at`). */
export function groupCdmoProfilesByCampaignStart<
  T extends { claim_campaign_started_at?: string | null },
>(profiles: T[]): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const profile of profiles) {
    const key = profile.claim_campaign_started_at;
    if (!key) continue;
    const list = map.get(key) ?? [];
    list.push(profile);
    map.set(key, list);
  }
  return map;
}

/** Batch-level CDMO claim status (mirrors sponsor batch rules). */
export function deriveCdmoBatchCampaignStatus(
  profiles: {
    campaign_active?: boolean | null;
    user_id?: string | null;
    claim_email_stage?: number | null;
  }[],
  options?: { anyUnsubscribed?: boolean },
): CampaignHistoryStatus | string {
  if (profiles.some((p) => p.user_id)) return 'stopped_account_created';
  if (options?.anyUnsubscribed) return 'stopped_unsubscribed';
  if (profiles.some((p) => p.campaign_active)) return 'active';
  if (
    profiles.length > 0 &&
    profiles.every((p) => (p.claim_email_stage ?? 0) >= 3)
  ) {
    return 'completed';
  }
  if (profiles.length > 0 && profiles.every((p) => !p.campaign_active)) {
    return 'completed';
  }
  return 'active';
}

/** Distinct CDMO batch keys (one admin “Start campaign” action per timestamp). */
export function collectDistinctCdmoBatchStarts(
  rows: { claim_campaign_started_at?: string | null }[],
): string[] {
  const seen = new Set<string>();
  const starts: string[] = [];
  for (const row of rows) {
    const key = row.claim_campaign_started_at;
    if (key && !seen.has(key)) {
      seen.add(key);
      starts.push(key);
    }
  }
  return starts;
}

export function buildCampaignHistoryIndex(
  sponsorCampaigns: { id: string; started_at: string }[],
  cdmoBatchStarts: string[],
): CampaignHistoryIndexEntry[] {
  const entries: CampaignHistoryIndexEntry[] = [
    ...sponsorCampaigns.map((c) => ({
      campaignType: 'sponsor_invite' as const,
      id: c.id,
      dateStarted: c.started_at,
    })),
    ...cdmoBatchStarts.map((startedAt) => ({
      campaignType: 'cdmo_claim' as const,
      id: `batch-${startedAt}`,
      dateStarted: startedAt,
    })),
  ];
  return entries.sort(
    (a, b) => new Date(b.dateStarted).getTime() - new Date(a.dateStarted).getTime(),
  );
}

export function filterCampaignHistoryIndex(
  entries: CampaignHistoryIndexEntry[],
  typeFilter: CampaignHistoryTypeFilter,
): CampaignHistoryIndexEntry[] {
  if (typeFilter === 'all') return entries;
  return entries.filter((e) => e.campaignType === typeFilter);
}

export function paginateCampaignHistoryIndex<T>(
  entries: T[],
  page: number,
  pageSize: number,
): { slice: T[]; totalCount: number; totalPages: number; page: number } {
  const totalCount = entries.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    slice: entries.slice(start, start + pageSize),
    totalCount,
    totalPages,
    page: safePage,
  };
}

export function chunkStrings(values: string[], size: number): string[][] {
  if (size <= 0) return [values];
  const chunks: string[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
}

export function buildCdmoBatchHistoryMetrics(
  items: {
    claim_email_stage?: number | null;
    claim_link_clicked_at?: string | null;
    user_id?: string | null;
    unsubscribed?: boolean | null;
  }[],
): CampaignHistoryMetrics {
  return items.reduce(
    (acc, row) => {
      const m = buildCdmoHistoryMetrics(row);
      return {
        sent: acc.sent + m.sent,
        linkClicks: acc.linkClicks + m.linkClicks,
        unsubscribed: acc.unsubscribed + m.unsubscribed,
        signUps: acc.signUps + m.signUps,
      };
    },
    { sent: 0, linkClicks: 0, unsubscribed: 0, signUps: 0 },
  );
}
